import React, { createContext, useState, useContext, useEffect } from 'react';
import axios, { setAuthToken } from '../utils/axios';

// Create the context
const AuthContext = createContext();

// Hook for using the context
export const useAuth = () => useContext(AuthContext);

// Provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    // Try to get token from localStorage on initial load
    const storedToken = localStorage.getItem('token');
    console.log('Initial token from localStorage:', storedToken ? `${storedToken.substring(0, 10)}...` : 'null');
    return storedToken;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Update auth state when token changes
  useEffect(() => {
    console.log('Token changed:', token ? `${token.substring(0, 10)}...` : 'null');
    if (token) {
      localStorage.setItem('token', token);
      const parsedUser = parseJwt(token);
      console.log('Parsed user from token:', parsedUser);
      setUser(parsedUser);
      
      // Set axios default headers for all requests
      setAuthToken(token);
      
      // Fetch complete user profile including role
      const fetchUserProfile = async () => {
        try {
          const response = await axios.get('/api/v1/auth/me');
          console.log('Fetched user profile:', response.data);
          // Update user with complete profile data including role
          setUser(prevUser => ({
            ...prevUser,
            ...response.data
          }));
        } catch (err) {
          console.error('Failed to fetch user profile:', err);
        }
      };
      
      fetchUserProfile();
    } else {
      localStorage.removeItem('token');
      setUser(null);
      console.log('Removed token from localStorage and reset user');
      
      // Remove auth header
      setAuthToken(null);
    }
    setLoading(false);
  }, [token]);

  // Parse JWT token to get user info
  const parseJwt = (token) => {
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
      return null;
    }
  };

  // Register new user
  const register = async (email, name, password) => {
    try {
      setError(null);
      console.log('Registering user:', email);
      const response = await axios.post('/api/v1/auth/register', {
        email,
        name,
        password
      });
      
      console.log('Registration response:', response.status, response.data);
      
      // On successful registration, login the user
      if (response.status === 201) {
        console.log('Registration successful, logging in...');
        await login(email, password);
      }
      
      return response.data;
    } catch (err) {
      console.error('Registration error:', err.response?.data || err.message);
      setError(err.response?.data?.detail || 'Registration failed');
      throw err;
    }
  };

  // Login user
  const login = async (email, password) => {
    try {
      setError(null);
      console.log('Logging in user:', email);
      // Use FormData as the API expects form data
      const formData = new URLSearchParams();
      formData.append('username', email);
      formData.append('password', password);
      
      const response = await axios.post('/api/v1/auth/login', formData.toString(), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });
      
      console.log('Login response:', response.status, response.data);
      
      if (response.data?.access_token) {
        console.log('Setting token with length:', response.data.access_token.length);
        setToken(response.data.access_token);
        
        // Set token in axios headers for subsequent requests
        setAuthToken(response.data.access_token);
        
        // Immediately fetch user profile to get role
        try {
          const profileResponse = await axios.get('/api/v1/auth/me');
          console.log('User profile response:', profileResponse.data);
          
          // Update user data with complete profile including role
          setUser(prevUser => ({
            ...prevUser,
            ...profileResponse.data
          }));
        } catch (profileErr) {
          console.error('Error fetching user profile:', profileErr);
        }
      } else {
        console.error('No access token in response');
      }
      
      return response.data;
    } catch (err) {
      console.error('Login error:', err.response?.data || err.message);
      setError(err.response?.data?.detail || 'Login failed');
      throw err;
    }
  };

  // Logout user
  const logout = () => {
    setToken(null);
  };

  // Get user profile
  const getUserProfile = async () => {
    try {
      setError(null);
      const response = await axios.get('/api/v1/users/me');
      // Update user information in state
      const updatedUser = {
        ...user,
        name: response.data.name,
        email: response.data.email
      };
      setUser(updatedUser);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to get user profile');
      throw err;
    }
  };

  // Update user profile
  const updateUserProfile = async (userData) => {
    try {
      setError(null);
      const response = await axios.put('/api/v1/users/me', userData);
      
      // Update user information in state if name or email was changed
      if (userData.name || userData.email) {
        const updatedUser = {
          ...user,
          ...(userData.name && { name: userData.name }),
          ...(userData.email && { email: userData.email })
        };
        setUser(updatedUser);
      }
      
      return response.data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update profile');
      throw err;
    }
  };

  // Provide the auth context
  return (
    <AuthContext.Provider
      value={{
        user,
        currentUser: user,  // Add currentUser alias for clarity
        token,
        loading,
        error,
        isAuthenticated: !!token,
        register,
        login,
        logout,
        getUserProfile,
        updateUserProfile
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};